import { ImpactType, pluralise } from "@ecologi/shared";
import styled from "@emotion/styled";
import Sapling from "@ecologi/assets/icons/sapling.svg";
import Flowers from "@ecologi/assets/icons/wildflower.svg";
import CarbonCloud from "@ecologi/assets/icons/carbon-cloud.svg";
import Turbine from "@ecologi/assets/icons/turbine.svg";
import React, { ReactNode } from "react";
import { css } from "@emotion/react";
import { extraSmallDown } from "../theme/ecologi-breakpoints";
import Decimal from "decimal.js";
import { CombinedImpactQuantities } from "../ImpactStats/ImpactStats";

interface StatCardProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  impactType: ImpactType;
  quantities: CombinedImpactQuantities;
  isLarge?: boolean;
}

const CardContainer = styled.button<{ isLarge: boolean }>`
  all: unset;
  cursor: pointer;
  background: ${({ theme }) => theme.colors.snowdrop};
  height: 164px;
  width: 164px;
  position: relative;
  border-radius: 30px;
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  transition: bottom 0.3s;
  bottom: 0px;
  &:focus-visible {
    outline: ${({ theme }) => `2px solid ${theme.colors.forest}`};
    outline-offset: 4px;
  }
  &:hover {
    bottom: 3px;
  }
  @media ${({ theme }) => theme.bp.extraSmallDown} {
    width: ${({ isLarge }) => (isLarge ? "152px" : "104px")};
    height: 136px;
    border-radius: 18px;
    justify-content: flex-start;
  }
`;

const DescriptionContainer = styled.div`
  line-height: 1.4;
  text-align: center;
`;

const QuantityLabel = styled.p`
  font-size: 26px;
  font-weight: 800;
  @media ${({ theme }) => theme.bp.extraSmallDown} {
    font-size: 18px;
  }
`;

const DescriptionLabel = styled.p`
  font-size: 14px;
  font-weight: 400;
  @media ${({ theme }) => theme.bp.extraSmallDown} {
    max-width: 80%;
    margin: auto;
  }
`;

const StyledSapling = styled(Sapling)`
  width: 98px;
  height: 100px;
  position: absolute;
  bottom: -8px;
  @media ${({ theme }) => theme.bp.extraSmallDown} {
    width: 59.5px;
    height: 64px;
    bottom: -4px;
  }
`;

const defaultStyles = css`
  width: 120px;
  height: 136px;
  position: absolute;
  bottom: -8px;

  @media ${extraSmallDown} {
    width: 76px;
    height: 81px;
    bottom: -4px;
  }
`;

const StyledTurbine = styled(Turbine)`
  ${defaultStyles}
`;

const StyledCarbonCloud = styled(CarbonCloud)`
  ${defaultStyles}
`;

const StyledFlowers = styled(Flowers)`
  ${defaultStyles}
`;

const IconContainer = styled.div`
  height: 45px;
  width: 45px;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ImpactTypeAsset: Record<
  ImpactType,
  {
    label: (quantity: number) => string;
    image: ReactNode;
  }
> = {
  [ImpactType.TREES]: {
    label: (quantity) => `${pluralise(quantity, "tree", false)} funded`,
    image: <StyledSapling />,
  },
  [ImpactType.CARBON_OFFSETS]: {
    label: () => "tCO2e avoided",
    image: <StyledTurbine />,
  },
  [ImpactType.CARBON_REMOVAL]: {
    label: () => "tCO2e removed",
    image: <StyledCarbonCloud />,
  },
  [ImpactType.HABITAT_RESTORATION]: {
    label: () => "m² habitat restored",
    image: <StyledFlowers />,
  },
};

export const ImpactStatCard = React.forwardRef<
  HTMLButtonElement,
  StatCardProps
>(
  (
    {
      impactType,
      quantities,
      onClick,
      isLarge = false,
      ...props
    }: StatCardProps,
    ref
  ) => (
    <CardContainer
      onClick={onClick}
      isLarge={isLarge}
      ref={ref}
      data-testid={`impact-stat-card-${impactType.toLowerCase()}`}
      {...props}
    >
      <IconContainer>{ImpactTypeAsset[impactType].image}</IconContainer>
      <DescriptionContainer>
        <QuantityLabel>
          {new Decimal(quantities.paid)
            .plus(quantities.pending)
            .toNumber()
            .toLocaleString(undefined, { maximumFractionDigits: 2 })}
        </QuantityLabel>
        <DescriptionLabel>
          {ImpactTypeAsset[impactType].label(quantities.paid)}
          {quantities.pending > 0 && <span>*</span>}
        </DescriptionLabel>
      </DescriptionContainer>
    </CardContainer>
  )
);
